import type {LogoutResponse, LogoutScope} from "@/src/modules/auth/types";

const LOGOUT_SCOPES = new Set<LogoutScope>(["current_device", "all_devices"]);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readLogoutScope(row: Record<string, unknown> | null): LogoutScope | undefined {
  if (!row) return undefined;
  const scope = row.logout_scope;
  return typeof scope === "string" && LOGOUT_SCOPES.has(scope as LogoutScope)
    ? (scope as LogoutScope)
    : undefined;
}

function readMessage(row: Record<string, unknown> | null): string | undefined {
  if (!row) return undefined;
  return typeof row.message === "string" ? row.message : undefined;
}

/** Parse `GET /auth/secure/logout` — supports flat body or `{ data: … }` envelope. */
export function parseLogoutResponse(body: unknown): LogoutResponse {
  const root = asRecord(body);
  if (!root) {
    return {};
  }

  const direct = readLogoutScope(root);
  if (direct) {
    return {
      message: readMessage(root),
      logout_scope: direct,
    };
  }

  const nested = readLogoutScope(asRecord(root.data));
  if (nested) {
    const data = asRecord(root.data);
    return {
      message: readMessage(data) ?? readMessage(root),
      logout_scope: nested,
    };
  }

  const message = readMessage(root) ?? readMessage(asRecord(root.data));
  if (message) {
    return {message};
  }

  return {};
}
